import { PDFEditorConnector } from '@ethsign/pdf-editor';
import { ethSignAesDecrypt, ethSignAesEncrypt, hashSha256 } from '@ethsign/utils-web';
import { uniqBy } from 'lodash-es';

import { queryContract, queryContractFile, queryRecipients, signContract } from '@/services';
import { getContractByApiKey } from '@/services/sign-sdk';
import { ContractItem, ContractType, ICurrentUser } from '@/types';
import {
  addAnnotationToPDF,
  convertXmlToJSON,
  deletePicBorder,
  download,
  downloadContractFile,
  getReadOnlyTextXml,
  getUserWalletType,
  reportGAEvent,
  WalletType
} from '@/utils';
import { ControllerBase } from '@/utils/bizify';
import { WalletFactory } from '@/utils/wallet/WalletFactory';

export type PageData = {
  contract: ContractItem;
  file: { fileString: string; annotation: string };
  modalOpen: boolean;
  loading: boolean;
  encryptFile: string;
  permissionModalOpen: boolean;
  fields: any;
  selectedField: string;
  password?: string;
  id: string;
  user: ICurrentUser;
  instance: PDFEditorConnector;
  displayPassword: string;
  email: string;
  xfdfStrings: string[] | any;
  subContractXfdfStrings: string[] | any;
  subContract: ContractItem;
  shareModalOpen: boolean;
  recipientsPage: number;
  recipientsHasMore: boolean;
  isPreview?: boolean;
  apiKey?: string;
};

export class PageCtrl extends ControllerBase<PageData> {
  protected $data(): any {
    return {
      contract: null,
      file: null,
      modalOpen: false,
      permissionModalOpen: false,
      loading: false,
      encryptFile: '',
      fields: [],
      selectedField: '',
      password: '',
      displayPassword: '',
      id: '',
      user: {},
      instance: null,
      email: '',
      xfdfStrings: [],
      subContractXfdfStrings: [],
      subContract: null,
      shareModalOpen: false,
      recipientsPage: 1,
      recipientsHasMore: false
    };
  }
  initInfo = (data: {
    id: any;
    user: any;
    email: string;
    isView: boolean;
    isPreview?: boolean;
    apiKey?: string;
    password?: string;
  }): void => {
    const { id, user, email, isView, isPreview, apiKey, password } = data;
    this.state.id = id;
    this.state.user = user;
    this.state.email = email;
    this.state.isPreview = isPreview;
    this.state.apiKey = apiKey;
    this.state.password = password;
    this.getData(id, email, isView);
  };
  getData = async (id: string, email: string, isView: boolean): Promise<void> => {
    this.state.loading = true;
    try {
      let contract;
      let file;
      if (this.state.isPreview && this.state.apiKey) {
        contract = await getContractByApiKey(id, this.state.apiKey);
        file = contract;
      } else {
        const res = await Promise.all([
          queryContract({
            id,
            email,
            mode: isView ? 'view' : '',
            errorHandler: (e) => {
              if (e.response.status === 400) {
                this.state.permissionModalOpen = true;
                this.state.loading = false;
              }
            }
          }),
          queryContractFile({
            id,
            email,
            errorHandler: (e) => {
              if (e.response.status === 400) {
                this.state.permissionModalOpen = true;
                this.state.loading = false;
              }
            }
          })
        ]);
        contract = res[0];
        file = res[1];
      }
      if (contract?.instanceContractId) {
        window.location.replace(`/contract/${contract.instanceContractId}${window.location.search}`);
        return;
      }

      this.state.contract = contract;
      if (
        (contract.contractType === ContractType.visual_contract && contract.clientStatus !== 'wait_me') ||
        contract.contractType === ContractType.link_sign_contract
      ) {
        await this.getRecipients(id);
      }
      this.state.encryptFile = file.fileContent;

      if (contract.enableEncryption) {
        this.state.loading = false;
        if (this.state.password) {
          try {
            const file = await ethSignAesDecrypt(this.state.encryptFile, this.state.password);
            this.passwordConfirm(JSON.parse(file), this.state.password);
          } catch (error) {
            this.state.modalOpen = true;
          }
        } else {
          this.state.modalOpen = true;
        }
      } else {
        const fileContent = file.fileContent && JSON.parse(file.fileContent);
        fileContent.annotation = await convertXmlToJSON(fileContent.annotation);

        this.state.file = fileContent;
        this.state.xfdfStrings = await this.getXfdfString(this.state.contract.recipients);
      }
    } catch (error) {
      console.log(error);
    }
  };

  getRecipients = async (id: string): Promise<void> => {
    try {
      this.state.loading = true;
      const recipients = await queryRecipients(id, this.state.recipientsPage);
      if (recipients) {
        this.state.recipientsPage += 1;
        this.state.contract.recipients = uniqBy(
          [
            ...(recipients?.rows || []).filter((item: any) => item.role === 'signer'),
            ...this.state.contract.recipients.filter((item: any) => item.role === 'signer')
          ],
          'address'
        );
        this.state.xfdfStrings = await this.getXfdfString(this.state.contract.recipients, this.state.password);
        this.state.contract.recipientsTotal = recipients?.total || 0;
      }
      if (recipients?.total <= this.state.contract.recipients?.length) {
        this.state.recipientsHasMore = false;
      } else {
        this.state.recipientsHasMore = true;
      }
      this.state.loading = false;
    } catch (error) {
      this.state.loading = false;
      console.log(error);
    }
  };
  getXfdfString = async (recipients: any[], password?: string): Promise<any[]> => {
    const signedRecipients = recipients.filter((item: any) => item.role === 'signer' && item.status === 'signed');
    if (password) {
      return await Promise.all(
        signedRecipients.map(async (item: any) => {
          let res = item.signingContent
            ? await ethSignAesDecrypt(JSON.parse(item.signingContent)?.signingContent, password)
            : '';
          res = await convertXmlToJSON(res);
          return res;
        })
      );
    } else {
      return await Promise.all(
        signedRecipients.map((item: any) =>
          convertXmlToJSON(item.signingContent ? JSON.parse(item.signingContent).signingContent : '')
        )
      );
    }
  };

  getContract = async (id: string, isNotModify?: boolean): Promise<any> => {
    if (isNotModify) {
      const res = {
        ...this.state.contract,
        id,
        recipients: [this.state.contract.recipients.find((item) => item.contractId === id)]
      };
      return res;
    }
    const subRecipients = [this.state.contract.recipients.find((item) => item.contractId === id)];
    this.state.subContract = { ...this.state.contract, id, recipients: subRecipients } as ContractItem;
    this.state.subContractXfdfStrings = await this.getXfdfString(subRecipients, this.state.password);
  };
  passwordConfirm = async (file: { fileString: string; annotation: string }, password: string): Promise<void> => {
    file.annotation = await convertXmlToJSON(file.annotation);
    this.state.file = file;
    this.state.password = password;
    this.state.modalOpen = false;
    this.state.loading = true;
    this.state.xfdfStrings = await this.getXfdfString(this.state.contract.recipients, password);
    this.state.subContractXfdfStrings =
      this.state.subContract && (await this.getXfdfString(this.state.subContract.recipients, this.state.password));
  };
  setLoading = (loading: boolean): void => {
    this.state.loading = loading;
  };
  setModalOpen = (open: boolean): void => {
    if (!open) {
      if (this.state.file) {
        this.state.modalOpen = false;
      }
    } else {
      this.state.modalOpen = open;
    }
  };

  setPermissionModalOpen = (open: boolean): void => {
    this.state.permissionModalOpen = open;
  };
  setFields = (data: unknown): void => {
    this.state.fields = data;
  };
  setSelectedField = (data: string): void => {
    console.log(data, 'select');

    this.state.selectedField = data;
  };
  submit = async (instance: PDFEditorConnector): Promise<boolean> => {
    if (!instance) return false;
    const { user, id, contract } = this.state;

    const currentAnotations = instance.getAllAnnotations().filter((annot: any) => annot.data.completed);
    const signAnotations = await Promise.all(
      currentAnotations.map(async (item: any) => {
        item.data = { ...item.data };
        if (item.data.type === 'signature') {
          item.bitmapFile = await deletePicBorder(item.bitmapFile);
        }
        item.data = undefined;
        delete item.bitmapId;
        return item;
      })
    );
    if (signAnotations.length === 0) {
      return false;
    }
    const xfdfString = JSON.stringify(signAnotations);
    const signingContent = contract?.enableEncryption
      ? await ethSignAesEncrypt(xfdfString, this.state.password || '')
      : xfdfString;
    const walletType = getUserWalletType(user!.chainType as WalletType, user!.authType);
    const wallet = await WalletFactory.getWallet(walletType, user.displayAddress);
    const isConnected = await wallet.connect(user.authType);
    if (!isConnected) return false;
    let emailSignData;
    if (this.state.email && this.state.contract?.emailSignMessageTemplate) {
      const emailMessage = this.state.contract.emailSignMessageTemplate
        ?.replace('{email}', this.state.email)
        .replace('{address}', this.state.user.address);
      emailSignData = await wallet.safeSign(emailMessage);
    }
    const statement = 'EthSign Next is requesting your digital signature for this contract.';
    const signatureDataHash = await hashSha256(signingContent);

    const message = JSON.stringify({ statement: statement, signatureDataHash }, null, '  ');
    let signData: any;
    try {
      // sign of EIP-712
      if ([WalletType.EVM, WalletType.StarkNet].includes(walletType as any) && wallet.signByEIP712) {
        const eip712Message = wallet.getSignEIP712Message({
          chainId: wallet.chainId,
          info: statement,
          hash: signatureDataHash
        });
        signData = await wallet.signByEIP712(eip712Message);
      } else {
        if (walletType === WalletType.PARTICLE && this.state.email) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
        signData = await wallet.safeSign(message);
      }
    } catch (error) {
      console.log(error);
      return false;
    }
    const { signature, message: fullMessage } = signData;

    this.state.loading = true;
    try {
      await signContract({
        signingData: JSON.stringify({ statement, signingContent, fullMessage }),
        signature,
        id,
        protocolTag: wallet.protocolTag,
        addressEmailBindingEmail: this.state.email,
        addressEmailBindingMessage: emailSignData?.message,
        addressEmailBindingSignature: emailSignData?.signature,
        walletName: wallet.authType,
        chainId: user.chainId + ''
      });
      reportGAEvent('pc_contract_signContract', {
        contractId: id,
        walletName: user.authType,
        address: user.address,
        network: user.chainType
      });
      // 最后一个签署
      if (contract?.recipients?.filter((it: any) => it.role === 'signer' && it.status === 'pending').length === 1) {
        reportGAEvent('pc_contract_lastSign', {
          contractId: id,
          walletName: user.authType,
          address: user.address,
          network: user.chainType
        });
      }
    } catch (error) {
      this.state.loading = false;
      return false;
    }

    this.state.loading = false;
    return true;
  };

  onFieldClick = (Id: string, instance: PDFEditorConnector): void => {
    if (!instance) return;
    instance.selectAnnotationById(Id);
    instance.jumpToAnnotationById(Id);
  };

  showPassword = (isShow: boolean): void => {
    if (isShow) {
      this.state.modalOpen = true;
    } else {
      this.state.displayPassword = '';
    }
  };

  setDisplayPassword = (password: string): void => {
    this.state.displayPassword = password;
    this.state.modalOpen = false;
  };

  downloadFile = async (id?: string): Promise<void> => {
    this.state.loading = true;
    try {
      if (!id) {
        const { recipients, contractType } = this.state.contract;
        const fileContent = this.state.encryptFile;
        await downloadContractFile({
          recipients,
          contractType,
          fileContent,
          password: this.state.password,
          data: {
            id: this.state.id,
            enableEncryption: this.state.contract.enableEncryption,
            title: this.state.contract.title
          }
        });
      } else {
        const contractType = this.state.contract.contractType;
        let contract;
        let xfdfStrings;
        if (contractType === ContractType.visual_contract) {
          if (this.state.subContract && id === this.state.subContract.id) {
            contract = this.state.subContract;
            xfdfStrings = [...this.state.subContractXfdfStrings];
          } else {
            contract = await this.getContract(id, true);
            xfdfStrings = await this.getXfdfString(contract.recipients, this.state.password);
          }
        } else {
          contract = this.state.contract;
          xfdfStrings = [...this.state.xfdfStrings];
          const annotationXml = await getReadOnlyTextXml(this.state.file.annotation);
          annotationXml && xfdfStrings.push(annotationXml);
        }
        const documentBuffer = await addAnnotationToPDF(this.state.file.fileString, xfdfStrings, {
          id: contract.id,
          enableEncryption: contract.enableEncryption,
          version: 1
        });
        download(documentBuffer, contract.title + '.pdf', 'application/pdf');
      }
    } catch (e) {
      console.error(e);
      this.state.loading = false;
    } finally {
      this.state.loading = false;
    }
  };
  onShare = async (): Promise<void> => {
    this.state.shareModalOpen = true;
  };

  setShareModalOpen = (open: boolean): void => {
    this.state.shareModalOpen = open;
  };
}
